import { useState, useEffect } from 'react';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import ChatInputArea from './ChatInputArea';
import { Message } from './chatTypes';
import { generateAIResponse } from '@/services/ai';

interface ChatInterfaceProps {
  chatbotId: string;
  chatbotName: string;
  chatbotAvatar: string;
  systemPrompt?: string;
}

const ChatInterface = ({ chatbotId, chatbotName, chatbotAvatar, systemPrompt }: ChatInterfaceProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    const storedMessages = localStorage.getItem(`chat_${chatbotId}`);
    
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    } else {
      setMessages([
        {
          id: '1',
          content: `Hello! I'm ${chatbotName}. How can I assist you today?`,
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    }
  }, [chatbotId, chatbotName]);
  
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(`chat_${chatbotId}`, JSON.stringify(messages));
    }
  }, [messages, chatbotId]);
  
  const handleSendMessage = async (messageText: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageText.trim(),
      isUser: true,
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      const { response, error } = await generateAIResponse(messages, systemPrompt);
      
      if (error) {
        console.error('AI response error:', error);
        throw new Error(error);
      }
      
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        isUser: false,
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botResponse]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'I apologize, but I encountered an error while processing your request. Please try again.',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const clearChat = () => {
    const confirmClear = window.confirm("Are you sure you want to clear this conversation?");
    if (confirmClear) {
      localStorage.removeItem(`chat_${chatbotId}`);
      setMessages([
        {
          id: '1',
          content: `Hello! I'm ${chatbotName}. How can I assist you today?`,
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-8rem)] bg-background rounded-xl shadow-sm border">
      <ChatHeader 
        chatbotName={chatbotName} 
        chatbotAvatar={chatbotAvatar} 
        onClearChat={clearChat} 
      />
      <MessageList 
        messages={messages}
        isLoading={isLoading}
        chatbotName={chatbotName}
        chatbotAvatar={chatbotAvatar}
      />
      <ChatInputArea 
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ChatInterface;
